import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';

const FALLBACK_COLORS = ['#0ea5e9', '#6366f1', '#f97316', '#ef4444', '#10b981', '#8b5cf6'];

function deriveInitial(user) {
  const source = (user?.displayName || user?.email || 'O').trim();
  return source ? source[0].toUpperCase() : 'O';
}

function colorFromInitial(initial) {
  const code = initial.charCodeAt(0);
  return FALLBACK_COLORS[code % FALLBACK_COLORS.length];
}

function LoginAvatar({ variant = 'floating' }) {
  const [userSnapshot, setUserSnapshot] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const isInline = variant === 'inline';

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUserSnapshot(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const { isAuthenticated, avatarInitial, avatarUrl, avatarColor } = useMemo(() => {
    if (!userSnapshot) {
      return {
        isAuthenticated: false,
        avatarInitial: 'O',
        avatarUrl: null,
        avatarColor: colorFromInitial('O'),
      };
    }

    const initial = deriveInitial(userSnapshot);
    return {
      isAuthenticated: true,
      avatarInitial: initial,
      avatarUrl: userSnapshot.photoURL || null,
      avatarColor: colorFromInitial(initial),
    };
  }, [userSnapshot]);

  useEffect(() => {
    if (!isAuthenticated) {
      setMenuOpen(false);
    }
  }, [isAuthenticated]);

  const toggleMenu = () => {
    if (isAuthenticated) {
      setMenuOpen((open) => !open);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } finally {
      setMenuOpen(false);
    }
  };

  const containerStyle = isInline
    ? {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }
    : {
        position: 'fixed',
        top: 'calc(var(--topbar-height, 64px) + 1rem)',
        right: '1.5rem',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '0.6rem',
      };

  const buttonBackground = avatarUrl
    ? (isInline
        ? 'linear-gradient(135deg, rgba(79, 156, 249, 0.35), rgba(47, 126, 230, 0.75))'
        : '#0284c7')
    : (isInline ? 'var(--color-surface-elevated)' : avatarColor);

  const buttonStyle = {
    width: isInline ? '42px' : '48px',
    height: isInline ? '42px' : '48px',
    borderRadius: '50%',
    background: buttonBackground,
    color: isInline && !avatarUrl ? 'var(--color-primary)' : '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: isInline
      ? '0 16px 28px -18px rgba(8, 12, 22, 0.85)'
      : '0 12px 30px rgba(2, 132, 199, 0.4)',
    fontWeight: 700,
    textDecoration: 'none',
    overflow: 'hidden',
    border: isInline ? '1px solid var(--color-border)' : 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  };

  const menuStyle = {
    width: '200px',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-border)',
    borderRadius: '12px',
    padding: '0.9rem',
    color: 'var(--color-text)',
    boxShadow: '0 26px 46px -20px rgba(8, 12, 22, 0.85)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    ...(isInline
      ? {
          position: 'absolute',
          top: 'calc(100% + 0.75rem)',
          right: 0,
          zIndex: 1000,
        }
      : {}),
  };

  const menuButtonBase = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.75rem',
    padding: '0.6rem 0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid transparent',
    textDecoration: 'none',
    transition: 'background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
  };

  const profileButtonStyle = {
    ...menuButtonBase,
    background: 'linear-gradient(135deg, rgba(79, 156, 249, 0.22), rgba(47, 126, 230, 0.38))',
    color: 'rgba(219, 234, 254, 0.95)',
    borderColor: 'rgba(79, 156, 249, 0.35)',
    boxShadow: '0 14px 28px -18px rgba(79, 156, 249, 0.55)',
  };

  const logoutButtonStyle = {
    ...menuButtonBase,
    background: 'linear-gradient(135deg, rgba(248, 113, 113, 0.2), rgba(239, 68, 68, 0.35))',
    color: '#fecaca',
    borderColor: 'rgba(248, 113, 113, 0.28)',
  };

  const profilePath = userSnapshot?.uid ? `/user?id=${encodeURIComponent(userSnapshot.uid)}` : '/user';

  if (!isAuthenticated) {
    return (
      <div style={containerStyle}>
        <Link
          to="/login"
          title="Log in to Origami"
          aria-label="Log in to Origami"
          style={buttonStyle}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Profile"
              referrerPolicy="no-referrer"
              style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ fontSize: '0.95rem', letterSpacing: '0.08em' }}>{avatarInitial}</span>
          )}
        </Link>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <button
        type="button"
        onClick={toggleMenu}
        title="Open profile menu"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        style={buttonStyle}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="Profile"
            referrerPolicy="no-referrer"
            style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          <span style={{ fontSize: '0.95rem', letterSpacing: '0.08em' }}>{avatarInitial}</span>
        )}
      </button>

      {menuOpen && (
        <div
          role="menu"
          style={menuStyle}
        >
          <div style={{ fontSize: '0.85rem', opacity: 0.85, wordBreak: 'break-word' }}>{userSnapshot.email}</div>
          <Link
            to={profilePath}
            role="menuitem"
            style={profileButtonStyle}
            onClick={() => setMenuOpen(false)}
          >
            View profile
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            role="menuitem"
            style={{
              ...logoutButtonStyle,
            }}
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

export default LoginAvatar;
